import { z } from "zod";

import { exactDecimalSchema } from "./faultContract";
import { formatApiError, requestSignal } from "./faultLink";
import { ApiError } from "./http";
import {
  busKindSchema,
  deviceKindSchema,
  feederKindSchema,
  loadKindSchema,
  projectRevisionStatusSchema,
  sldStatusSchema,
  sldWarningCodeSchema,
  sourceKindSchema,
  structuralErrorCodeSchema,
} from "./sldContract";

// The single-line diagram of a project revision (SLD-a, Master Prompt A26).
//
// The model mirrors backend/app/schemas/sld.py one to one: every voltage and rating is an
// exact decimal string (a binary float is refused), every code non-blank, no unknown key.
// One document per revision, editable while the revision is OPEN; the editor sends back the
// version it loaded and the server answers 409 when the diagram changed meanwhile. A save
// that breaks a structural rule is refused with every error at once (422), typed here as
// StructuralSaveError so the validation panel can list them.

const PROJECTS = "/api/v1/projects";
const UNEXPECTED = "Unexpected response from the KES Electrical OS single-line diagram API.";

const code = z.string().trim().min(1).max(50);
const name = z.string().trim().min(1).max(200);
const optionalCode = code.nullish();

// A choice is optional here only so a missing one is reported by the rule below as a
// missing value ("... is required."), the way the study forms report their choices; the
// backend requires every kind.
function requireChoice(field: string, value: unknown, ctx: z.RefinementCtx): void {
  if (value === undefined) {
    ctx.addIssue({
      code: "invalid_type",
      expected: "string",
      received: "undefined",
      message: `${field} is required`,
      path: [field],
    });
  }
}

const requireKind = (values: { kind?: string | undefined }, ctx: z.RefinementCtx): void =>
  requireChoice("kind", values.kind, ctx);

// -- the model as the editor sends it -----------------------------------------------------

export const busRequestSchema = z
  .object({
    code,
    name,
    nominal_voltage_v: exactDecimalSchema,
    kind: busKindSchema.optional(),
  })
  .strict()
  .superRefine(requireKind);

// A generator or a UPS may carry a rating in kVA, a PV source an AC rating in kW: optional
// descriptors the sizing studies compare with their own selected rating (A28 (d)).
export const sourceRequestSchema = z
  .object({
    code,
    kind: sourceKindSchema.optional(),
    voltage_v: exactDecimalSchema,
    rating_kva: exactDecimalSchema.nullish(),
    rating_kw: exactDecimalSchema.nullish(),
  })
  .strict()
  .superRefine((values, ctx) => {
    requireKind(values, ctx);
    if (values.rating_kva != null && values.kind !== "GENERATOR" && values.kind !== "UPS") {
      ctx.addIssue({
        code: "custom",
        message: "rating_kva belongs to a GENERATOR or a UPS source",
        path: ["rating_kva"],
      });
    }
    if (values.rating_kw != null && values.kind !== "PV") {
      ctx.addIssue({
        code: "custom",
        message: "rating_kw (AC) belongs to a PV source",
        path: ["rating_kw"],
      });
    }
  });

export const deviceRequestSchema = z
  .object({
    code,
    kind: deviceKindSchema.optional(),
    rating_a: exactDecimalSchema,
  })
  .strict()
  .superRefine(requireKind);

export const cableRequestSchema = z
  .object({
    code,
    length_m: exactDecimalSchema,
  })
  .strict();

// A transformer's optional device is its secondary incomer breaker at to_bus (the ACB
// between the transformer and its PCC), a descriptor like a source incomer's device.
export const transformerRequestSchema = z
  .object({
    code,
    from_bus: code,
    to_bus: code,
    rating_kva: exactDecimalSchema,
    primary_v: exactDecimalSchema,
    secondary_v: exactDecimalSchema,
    device: deviceRequestSchema.nullish(),
  })
  .strict();

// A feeder or a busbar trunking ends at exactly one of to_bus or to_load; the device and
// the cable are descriptors the engineer typed, both optional.
export const feederRequestSchema = z
  .object({
    code,
    kind: feederKindSchema.optional(),
    from_bus: code,
    to_bus: optionalCode,
    to_load: optionalCode,
    device: deviceRequestSchema.nullish(),
    cable: cableRequestSchema.nullish(),
  })
  .strict()
  .superRefine((values, ctx) => {
    requireKind(values, ctx);
    if ((values.to_bus == null) === (values.to_load == null)) {
      ctx.addIssue({
        code: "custom",
        message: "a feeder ends at exactly one of to_bus or to_load",
        path: ["to_bus"],
      });
    }
  });

export const sourceIncomerRequestSchema = z
  .object({
    code,
    source: code,
    to_bus: code,
    device: deviceRequestSchema.nullish(),
  })
  .strict();

export const loadRequestSchema = z
  .object({
    code,
    kind: loadKindSchema.optional(),
    rated_kw: exactDecimalSchema,
  })
  .strict()
  .superRefine(requireKind);

export const sldModelRequestSchema = z
  .object({
    code,
    name,
    buses: z.array(busRequestSchema),
    sources: z.array(sourceRequestSchema),
    transformers: z.array(transformerRequestSchema),
    feeders: z.array(feederRequestSchema),
    source_incomers: z.array(sourceIncomerRequestSchema),
    loads: z.array(loadRequestSchema),
    notes: z.string().trim().max(1000).nullish(),
  })
  .strict();

// -- the document as the server answers it ------------------------------------------------

export const busResponseSchema = z
  .object({
    code: z.string(),
    name: z.string(),
    nominal_voltage_v: z.string(),
    kind: busKindSchema,
  })
  .strict();

export const sourceResponseSchema = z
  .object({
    code: z.string(),
    kind: sourceKindSchema,
    voltage_v: z.string(),
    // null from the API; absent in a canonical model stored before a source could carry one.
    rating_kva: z.string().nullish(),
    rating_kw: z.string().nullish(),
  })
  .strict();

export const deviceResponseSchema = z
  .object({
    code: z.string(),
    kind: deviceKindSchema,
    rating_a: z.string(),
  })
  .strict();

export const cableResponseSchema = z
  .object({
    code: z.string(),
    length_m: z.string(),
  })
  .strict();

export const transformerResponseSchema = z
  .object({
    code: z.string(),
    from_bus: z.string(),
    to_bus: z.string(),
    rating_kva: z.string(),
    primary_v: z.string(),
    secondary_v: z.string(),
    // null from the API; absent in a canonical model stored before transformers had one.
    device: deviceResponseSchema.nullish(),
  })
  .strict();

export const feederResponseSchema = z
  .object({
    code: z.string(),
    kind: feederKindSchema,
    from_bus: z.string(),
    to_bus: z.string().nullable(),
    to_load: z.string().nullable(),
    device: deviceResponseSchema.nullable(),
    cable: cableResponseSchema.nullable(),
  })
  .strict();

export const sourceIncomerResponseSchema = z
  .object({
    code: z.string(),
    source: z.string(),
    to_bus: z.string(),
    device: deviceResponseSchema.nullable(),
  })
  .strict();

export const loadResponseSchema = z
  .object({
    code: z.string(),
    kind: loadKindSchema,
    rated_kw: z.string(),
  })
  .strict();

export const sldModelResponseSchema = z
  .object({
    code: z.string(),
    name: z.string(),
    buses: z.array(busResponseSchema),
    sources: z.array(sourceResponseSchema),
    transformers: z.array(transformerResponseSchema),
    feeders: z.array(feederResponseSchema),
    source_incomers: z.array(sourceIncomerResponseSchema),
    loads: z.array(loadResponseSchema),
    notes: z.string().nullable(),
  })
  .strict();

export const sldWarningSchema = z
  .object({
    code: sldWarningCodeSchema,
    element: z.string(),
    message: z.string(),
  })
  .strict();

export const sldCheckSchema = z
  .object({
    status: sldStatusSchema,
    warnings: z.array(sldWarningSchema),
  })
  .strict();

/** One structural error that refused a save: the rule, the element, the sentence. */
export const structuralErrorSchema = z
  .object({
    code: structuralErrorCodeSchema,
    element: z.string(),
    message: z.string(),
  })
  .strict();

// A revision without a saved diagram answers an empty model at version 0 with no hash and
// no audit fields; the editor then sends expected_version 0 on its first save.
export const sldDocumentSchema = z
  .object({
    project_revision_id: z.string().uuid(),
    revision_status: projectRevisionStatusSchema,
    model: sldModelResponseSchema,
    version: z.number().int().nonnegative(),
    content_hash: z.string().nullable(),
    updated_by: z.string().nullable(),
    updated_at: z.string().nullable(),
    check: sldCheckSchema,
  })
  .strict();

// The 422 body of a structural refusal (backend/app/api/v1/sld.py): a message and the list.
const structuralRefusalSchema = z
  .object({
    detail: z
      .object({
        message: z.string(),
        errors: z.array(structuralErrorSchema),
      })
      .strict(),
  })
  .strict();

export type BusRequest = z.infer<typeof busRequestSchema>;
export type SourceRequest = z.infer<typeof sourceRequestSchema>;
export type DeviceRequest = z.infer<typeof deviceRequestSchema>;
export type CableRequest = z.infer<typeof cableRequestSchema>;
export type TransformerRequest = z.infer<typeof transformerRequestSchema>;
export type FeederRequest = z.infer<typeof feederRequestSchema>;
export type SourceIncomerRequest = z.infer<typeof sourceIncomerRequestSchema>;
export type LoadRequest = z.infer<typeof loadRequestSchema>;
export type SldModelRequest = z.infer<typeof sldModelRequestSchema>;
export type SldModelResponse = z.infer<typeof sldModelResponseSchema>;
export type FeederResponse = z.infer<typeof feederResponseSchema>;
export type SldWarning = z.infer<typeof sldWarningSchema>;
export type SldCheck = z.infer<typeof sldCheckSchema>;
export type StructuralError = z.infer<typeof structuralErrorSchema>;
export type SldDocument = z.infer<typeof sldDocumentSchema>;

/** A save refused by the structural rules: every error, for the validation panel. */
export class StructuralSaveError extends ApiError {
  readonly errors: readonly StructuralError[];

  constructor(message: string, errors: readonly StructuralError[]) {
    super(message, 422);
    this.name = "StructuralSaveError";
    this.errors = errors;
  }
}

function sldUrl(projectId: string, revisionId: string, suffix = ""): string {
  const project = encodeURIComponent(projectId);
  const revision = encodeURIComponent(revisionId);
  return `${PROJECTS}/${project}/revisions/${revision}/sld${suffix}`;
}

async function requestDocument(
  url: string,
  init: { method: "GET" | "PUT" | "POST"; body?: unknown; signal?: AbortSignal | undefined },
): Promise<SldDocument> {
  const hasBody = init.body !== undefined;

  const response = await fetch(url, {
    method: init.method,
    headers: hasBody
      ? { Accept: "application/json", "Content-Type": "application/json" }
      : { Accept: "application/json" },
    ...(hasBody ? { body: JSON.stringify(init.body) } : {}),
    cache: "no-store",
    credentials: "same-origin",
    signal: requestSignal(init.signal),
  });

  const data: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    if (response.status === 422) {
      const refusal = structuralRefusalSchema.safeParse(data);
      if (refusal.success) {
        throw new StructuralSaveError(refusal.data.detail.message, refusal.data.detail.errors);
      }
    }
    throw new ApiError(
      formatApiError(data, response.status, "Single-line diagram"),
      response.status,
    );
  }

  const parsed = sldDocumentSchema.safeParse(data);

  if (!parsed.success) {
    throw new Error(UNEXPECTED);
  }

  return parsed.data;
}

/** The diagram of the revision; an empty model at version 0 when none was saved yet. */
export async function getSld(
  projectId: string,
  revisionId: string,
  signal?: AbortSignal,
): Promise<SldDocument> {
  return requestDocument(sldUrl(projectId, revisionId), { method: "GET", signal });
}

/**
 * Save the diagram of the open revision against the version the editor loaded. An unchanged
 * model keeps its version; a changed one bumps it. Throws StructuralSaveError with every
 * structural error (422), an ApiError with the server's sentence for a stale version or a
 * closed revision (409).
 */
export async function saveSld(
  projectId: string,
  revisionId: string,
  model: SldModelRequest,
  expectedVersion: number,
  signal?: AbortSignal,
): Promise<SldDocument> {
  const validatedModel = sldModelRequestSchema.parse(model);

  return requestDocument(sldUrl(projectId, revisionId), {
    method: "PUT",
    body: { model: validatedModel, expected_version: expectedVersion },
    signal,
  });
}

/** Start the open revision's diagram from the nearest earlier revision that has one. */
export async function copySldFromPrevious(
  projectId: string,
  revisionId: string,
  signal?: AbortSignal,
): Promise<SldDocument> {
  return requestDocument(sldUrl(projectId, revisionId, "/copy-from-previous"), {
    method: "POST",
    signal,
  });
}
